import { MutationTree } from "vuex";
import Vue from "vue";
import { IPropertyEditState } from "../property-states.store";
import propertyVideoTypesStore from "./property-video-types.store";
import { IPropertyVideoModel } from "../../../../models/property";
import { itemStateConstant } from "../../../../common";

const videoMutations: MutationTree<IPropertyEditState> = <MutationTree<IPropertyEditState>>{
	[propertyVideoTypesStore.Set.LIST_OF_PROPERTY_VIDEOS]: (state: IPropertyEditState, listOfPropertyVideos: IPropertyVideoModel[]) => {
		// Use Vue.set to ensure reactivity when Videos property doesn't exist
		Vue.set(state.property, 'Videos', listOfPropertyVideos);
	}
	, [propertyVideoTypesStore.Set.ADD_TO_LIST]: (state: IPropertyEditState, videos: IPropertyVideoModel[]) => {
        const currentVideos = Array.isArray(state.property.Videos) ? state.property.Videos : [];
        const next = Array.isArray(videos) ? videos : [];
        const currentPropertyId = state.property?.Id;

        // Check if incoming videos belong to current property
        const incomingPropertyId = next.length > 0 ? next[0].ObjectId : null;

        if (incomingPropertyId && currentPropertyId && incomingPropertyId !== currentPropertyId) {
            // Incoming videos are for a different property - ignore them
            return;
        }

        // Helper function to check if two videos match
        const videosMatch = (a: IPropertyVideoModel, b: IPropertyVideoModel): boolean => {
            // Must be same property
            if (a.ObjectId && b.ObjectId && a.ObjectId !== b.ObjectId) return false;

            // Match by database ID (exact same record)
            // Only match positive IDs - negative IDs are temporary and should not match each other
            if (a.Id && b.Id && a.Id > 0 && b.Id > 0 && a.Id === b.Id) return true;

            // For VideoUid and VideoId matches, also require same Order (slot)
            // This allows the same video to be used in different slots (Virtual 1 vs Virtual 2)
            if (a.VideoUid && b.VideoUid && a.VideoUid === b.VideoUid &&
                a.Order !== undefined && b.Order !== undefined && a.Order === b.Order) {
                return true;
            }
            if (a.VideoId && b.VideoId && a.VideoId === b.VideoId &&
                a.Order !== undefined && b.Order !== undefined && a.Order === b.Order) {
                return true;
            }

            // Match by Order (slot) only if both have ObjectId matching - for new entries without IDs
            if (a.ObjectId && b.ObjectId && a.ObjectId === b.ObjectId &&
                a.Order !== undefined && b.Order !== undefined && a.Order === b.Order) {
                return true;
            }
            return false;
        };

        // Start with a copy of current videos
        const merged: IPropertyVideoModel[] = [...currentVideos];

        // First, process all non-deleted videos to add/update them
        const activeVideos = next.filter(v => v.ItemState !== itemStateConstant.Deleted);
        const deletedVideos = next.filter(v => v.ItemState === itemStateConstant.Deleted);

        // Process active videos first
        activeVideos.forEach(video => {
            const matchIndex = merged.findIndex(item => videosMatch(item, video));

            if (matchIndex === -1) {
                // New video, add it
                merged.push(video);
            } else {
                // Existing video, merge/update it
                const existing = merged[matchIndex];
                const mergedVideo: IPropertyVideoModel = { ...existing };
                Object.keys(video || {}).forEach((key: keyof IPropertyVideoModel) => {
                    const value = video[key];
                    if (value !== undefined) {
                        mergedVideo[key] = value as never;
                    }
                });
                merged[matchIndex] = mergedVideo;
            }
        });

        // Then process deleted videos - match by Id first, then VideoUid/VideoId + Order
        // Collect indices to remove (for 'Added' videos that were never saved to server)
        const indicesToRemove: number[] = [];

        deletedVideos.forEach(video => {
            const matchIndex = merged.findIndex(item => {
                // Primary match by Id (most reliable - unique per slot)
                if (item.Id && video.Id && item.Id === video.Id) return true;
                // For VideoUid and VideoId, also require same Order to handle same video in different slots
                if (item.VideoUid && video.VideoUid && item.VideoUid === video.VideoUid &&
                    item.Order !== undefined && video.Order !== undefined && item.Order === video.Order) {
                    return true;
                }
                if (item.VideoId && video.VideoId && item.VideoId === video.VideoId &&
                    item.Order !== undefined && video.Order !== undefined && item.Order === video.Order) {
                    return true;
                }
                return false;
            });

            if (matchIndex !== -1) {
                const existingVideo = merged[matchIndex];
                if (existingVideo.ItemState === itemStateConstant.Added) {
                    // Video was never saved to server - remove it entirely
                    indicesToRemove.push(matchIndex);
                } else {
                    // Video exists on server - mark as deleted for sync
                    merged[matchIndex] = { ...existingVideo, ItemState: itemStateConstant.Deleted };
                }
            }
        });

        // Remove 'Added' videos from array (in reverse order to maintain indices)
        indicesToRemove.sort((a, b) => b - a).forEach(index => {
            merged.splice(index, 1);
        });

        // Sort by Order to ensure array index matches Order value
        merged.sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));

        state.property.Videos = merged;
	},
    [propertyVideoTypesStore.Set.DELETE_PROPERTY_VIDEO]: (state: IPropertyEditState, id: number) => {
        // UPDATE ITEM IF THIS IS ITEM HAD BE STORED IN THE SERVICE
		// DELETE ITEM IF THIS IS ITEM HAD JUST ADDED
		let item = state.property.Videos?.find(x => x.Id === id);
        if (!item) return;
		if (item.ItemState === itemStateConstant.Added) {
			let index = state.property.Videos.indexOf(item);
			state.property.Videos.splice(index, 1);
		} else {
			item.ItemState = itemStateConstant.Deleted;
		}
    }
}

export default videoMutations;
